import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  ToastContext,
  type ToastContextValue,
  type ToastKind,
} from "./useToast";

/**
 * Système de toasts non-bloquants (Sprint X-1).
 *
 * Remplace les `alert()` et les `window.confirm()` éligibles (les destructifs
 * gardent confirm pour la safety). Le hook `useToast()` est dans
 * `./useToast.ts` (split obligatoire pour react-refresh/only-export-components).
 *
 * Usage :
 *   const toast = useToast();
 *   toast.success("Enregistré");
 *   toast.error("Échec : " + err.message);
 *
 * Le `<ToastProvider>` doit envelopper l'app (cf. main.tsx).
 */

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

const DEFAULT_DURATION_MS: Record<ToastKind, number> = {
  success: 2500,
  info: 3500,
  error: 6000, // les erreurs restent un peu plus longtemps
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback<ToastContextValue["show"]>(
    (kind, message, opts) => {
      const id = ++idRef.current;
      setToasts((prev) => [...prev, { id, kind, message }]);
      const duration = opts?.durationMs ?? DEFAULT_DURATION_MS[kind];
      window.setTimeout(() => dismiss(id), duration);
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      show,
      success: (m, o) => show("success", m, o),
      error: (m, o) => show("error", m, o),
      info: (m, o) => show("info", m, o),
    }),
    [show],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: number) => void;
}) {
  if (toasts.length === 0) return null;
  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 max-w-sm w-[calc(100%-2rem)] sm:w-auto"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const palette: Record<ToastKind, { bg: string; border: string; dot: string; icon: string }> = {
    success: {
      bg: "bg-emerald-50",
      border: "border-emerald-200",
      dot: "bg-emerald-500",
      icon: "✓",
    },
    error: {
      bg: "bg-red-50",
      border: "border-red-200",
      dot: "bg-red-500",
      icon: "✕",
    },
    info: {
      bg: "bg-blue-50",
      border: "border-blue-200",
      dot: "bg-blue-500",
      icon: "ⓘ",
    },
  };

  const p = palette[toast.kind];
  return (
    <div
      role="status"
      className={
        `rounded-lg border ${p.bg} ${p.border} shadow-sm px-3 py-2.5 flex items-start gap-2.5 ` +
        `text-sm transition-all duration-200 ` +
        (entered ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0")
      }
    >
      <span
        aria-hidden
        className={`mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-xs ${p.dot}`}
      >
        {p.icon}
      </span>
      <p className="flex-1 leading-snug">{toast.message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Fermer"
        className="text-[color:var(--color-alto-muted)] hover:text-[color:var(--color-alto-ink)] text-lg leading-none"
      >
        ×
      </button>
    </div>
  );
}
